/**
 * Invariant-checked bundle lookups shared by every pipeline stage.
 *
 * @remarks
 * A room or item id that reaches the engine ALWAYS came from the validated
 * pack (schema gate) with its references cross-checked (semantic gate) — so
 * a failed lookup is a corrupt bundle or a programmer error, never a
 * player-facing refusal, and throws {@link EngineInvariantError}
 * (architecture.md §3.3). Funneling every stage through these helpers keeps
 * that judgment in one place (the DRY contract); {@link requireIndexed} is
 * the one throw they all share.
 *
 * @packageDocumentation
 */

import type {
	ActorDefinition,
	ActorId,
	AudioCueLayer,
	AudioDefinition,
	AudioId,
	CounterDefinition,
	CounterId,
	ItemDefinition,
	ItemId,
	PromptDefinition,
	PromptId,
	RoomDefinition,
	RoomId,
} from '@orpheus/content-schema';

import type { IndexedPack, LivePage } from './engine-game-bundle.js';

import { EngineInvariantError } from '../execution/engine-invariant-error.js';

/**
 * Read one index the pack promises to have filled.
 *
 * @param index - The index to read
 * @param id - The content id
 * @param kind - What the id names, for the message ("Item", "Room")
 * @returns The indexed value
 */
const requireIndexed = <Id extends string, Value>(index: ReadonlyMap<Id, Value>, id: Id, kind: string): Value => {
	const value = index.get(id);

	if (value === undefined) {
		throw new EngineInvariantError(
			`${kind} id "${id}" has no definition in the bundle — the loader's semantic gate should have rejected this`,
		);
	}

	return value;
};

/**
 * Look up an item the pack promises exists.
 *
 * @param bundle - The loaded bundle
 * @param itemId - The item's content id
 * @returns The item's definition
 */
export const requireItem = (bundle: IndexedPack, itemId: ItemId): ItemDefinition =>
	requireIndexed(bundle.indexes.itemsById, itemId, 'Item');

/**
 * D244: look up a counter the pack promises exists.
 *
 * @param bundle - The loaded bundle
 * @param counterId - The counter's content id
 * @returns The counter's declaration
 */
export const requireCounter = (bundle: IndexedPack, counterId: CounterId): CounterDefinition =>
	requireIndexed(bundle.indexes.counterDefinitionsById, counterId, 'Counter');

/**
 * Look up a room the pack promises exists.
 *
 * @param bundle - The loaded bundle
 * @param roomId - The room's content id
 * @returns The room's definition
 */
export const requireRoom = (bundle: IndexedPack, roomId: RoomId): RoomDefinition =>
	requireIndexed(bundle.indexes.roomsById, roomId, 'Room');

export const requireAudio = (bundle: IndexedPack, audioId: AudioId): AudioDefinition =>
	requireIndexed(bundle.indexes.audioById, audioId, 'Audio');

/** D264: one cue, as a host plays it — the loader refuses a bed named as a cue, so a bed here is a corrupt bundle. */
export interface CueAsset {
	readonly audioId: AudioId;
	readonly layer: AudioCueLayer;
	readonly src: string;
}

export const requireCue = (bundle: IndexedPack, audioId: AudioId): CueAsset => {
	const audio = requireAudio(bundle, audioId);

	if (audio.playback.kind !== 'cue') {
		throw new EngineInvariantError(`Audio id "${audioId}" is a ${audio.playback.kind}, not a cue — the loader's audio gate should have rejected this`);
	}

	return { audioId, layer: audio.playback.layer, src: audio.src };
};

/**
 * The words an item's own page uses (D205) — indexed for every item at load,
 * so an item that exists has them.
 *
 * @param bundle - The loaded bundle
 * @param itemId - The item's content id
 * @returns The folded words
 */
export const descriptionWordsOfItem = (bundle: IndexedPack, itemId: ItemId): readonly string[] =>
	requireIndexed(bundle.indexes.descriptionWordsByItemId, itemId, 'Item');

/**
 * The words a room's own page uses (D205) — indexed for every room at load.
 *
 * @param bundle - The loaded bundle
 * @param roomId - The room's content id
 * @returns The folded words
 */
export const descriptionWordsOfRoom = (bundle: IndexedPack, roomId: RoomId): readonly string[] =>
	requireIndexed(bundle.indexes.descriptionWordsByRoomId, roomId, 'Room');

/**
 * An item's page as the turn reads it (D216) — indexed for every item at load.
 *
 * @param bundle - The loaded bundle
 * @param itemId - The item's content id
 * @returns Its steady words and conditional readings
 */
export const livePageOfItem = (bundle: IndexedPack, itemId: ItemId): LivePage =>
	requireIndexed(bundle.indexes.livePageByItemId, itemId, 'Item');

/**
 * A room's page as the turn reads it (D216) — indexed for every room at load.
 *
 * @param bundle - The loaded bundle
 * @param roomId - The room's content id
 * @returns Its steady words and conditional readings
 */
export const livePageOfRoom = (bundle: IndexedPack, roomId: RoomId): LivePage =>
	requireIndexed(bundle.indexes.livePageByRoomId, roomId, 'Room');

/**
 * Look up an actor the pack promises exists (D232).
 *
 * @param bundle - The loaded bundle
 * @param actorId - The actor's content id
 * @returns The actor's definition
 */
export const requireActor = (bundle: IndexedPack, actorId: ActorId): ActorDefinition =>
	requireIndexed(bundle.indexes.actorsById, actorId, 'Actor');

/**
 * Look up a prompt an actor promises it declares (D235).
 *
 * @param actor - The actor asking
 * @param promptId - The prompt's content id
 * @returns The prompt's definition
 */
export const requirePrompt = (actor: ActorDefinition, promptId: PromptId): PromptDefinition => {
	const prompt = actor.prompts?.find((candidate) => candidate.promptId === promptId);

	if (prompt === undefined) {
		throw new EngineInvariantError(
			`Actor "${actor.actorId}" declares no prompt "${promptId}" — the loader's reference walk should have rejected this bundle`,
		);
	}

	return prompt;
};

/**
 * The words an actor's own page uses (D205's rung, D232) — indexed for every
 * actor at load.
 *
 * @param bundle - The loaded bundle
 * @param actorId - The actor's content id
 * @returns The folded words
 */
export const descriptionWordsOfActor = (bundle: IndexedPack, actorId: ActorId): readonly string[] =>
	requireIndexed(bundle.indexes.descriptionWordsByActorId, actorId, 'Actor');

/**
 * An actor's page as the turn reads it (D216's split, D232) — indexed for
 * every actor at load.
 *
 * @param bundle - The loaded bundle
 * @param actorId - The actor's content id
 * @returns Its steady words and conditional readings
 */
export const livePageOfActor = (bundle: IndexedPack, actorId: ActorId): LivePage =>
	requireIndexed(bundle.indexes.livePageByActorId, actorId, 'Actor');
